import { useMemo } from 'react';
import { Select, Button, FloatingField } from './';

export interface PaginationProps {
  /** 現在のページ（1始まり） */
  currentPage: number;
  /** 総ページ数 */
  totalPages: number;
  /** 1ページあたりの表示件数 */
  pageSize: number;
  /** 表示件数の選択肢 */
  pageSizeOptions?: number[];
  /** ページ変更時のコールバック */
  onPageChange: (page: number) => void;
  /** 表示件数変更時のコールバック */
  onPageSizeChange?: (pageSize: number) => void;
  /** 追加のクラス名 */
  className?: string;
}

/**
 * ページネーションコンポーネント
 */
export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  pageSize,
  pageSizeOptions = [10, 20, 50, 100],
  onPageChange,
  onPageSizeChange,
  className = '',
}) => {
  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSize = parseInt(e.target.value, 10);
    onPageSizeChange?.(newSize);
  };

  const handlePageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newPage = parseInt(e.target.value, 10);
    onPageChange(newPage);
  };

  const pageSizeSelectOptions = useMemo(
    () => pageSizeOptions.map((size) => ({ value: size.toString(), label: size.toString() })),
    [pageSizeOptions]
  );

  const pageNumberOptions = useMemo(
    () =>
      Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => ({
        value: page.toString(),
        label: `${page}/${totalPages}`,
      })),
    [totalPages]
  );

  return (
    <div className={`aip-table__footer-pagination ${className}`.trim()}>
      {onPageSizeChange && (
        <FloatingField label="表示件数" htmlFor="page-size-select">
          <Select
            id="page-size-select"
            value={pageSize.toString()}
            onChange={handlePageSizeChange}
            options={pageSizeSelectOptions}
            style={{ minWidth: 'calc((10 / 16 * 1rem) * 2 + 4rem)' }}
          />
        </FloatingField>
      )}

      <Button
        buttonType="icon-only"
        size="md"
        onClick={() => onPageChange(1)}
        disabled={currentPage <= 1}
        aria-label="最初のページ"
      >
        <span className="material-symbols-outlined">first_page</span>
      </Button>

      <Button
        buttonType="icon-only"
        size="md"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage <= 1}
        aria-label="前のページ"
      >
        <span className="material-symbols-outlined">chevron_left</span>
      </Button>

      <FloatingField label="ページ" htmlFor="page-number-select">
        <Select
          id="page-number-select"
          value={currentPage.toString()}
          onChange={handlePageChange}
          options={pageNumberOptions}
          style={{ minWidth: 'calc((10 / 16 * 1rem) * 2 + 4rem)' }}
        />
      </FloatingField>

      <Button
        buttonType="icon-only"
        size="md"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage >= totalPages}
        aria-label="次のページ"
      >
        <span className="material-symbols-outlined">chevron_right</span>
      </Button>

      <Button
        buttonType="icon-only"
        size="md"
        onClick={() => onPageChange(totalPages)}
        disabled={currentPage >= totalPages}
        aria-label="最後のページ"
      >
        <span className="material-symbols-outlined">last_page</span>
      </Button>
    </div>
  );
};

export default Pagination;

